import { LeftOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Participant } from "interface/communications"
import { useFlag } from "modules/flags/flags"
import { useMemo } from "react"
import { usePatientDetails } from "services/patients"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"
import { ChatInput } from "./ChatInput"
import { ChatList } from "./ChatList"
import { ChatContextType, ChatProvider } from "./ChatProvider"

export function PatientOmnichannel() {
	const flags = useFlag("chatMessageInApp")
	const { data: sender, isFetched } = useUserRole()
	const { data: patient } = usePatientDetails(sender?.id)
	const activeParticipant = useMemo(() => {
		if (!patient) {
			return undefined
		}

		return {
			id: patient.id,
			fullName: patient.fullName,
			displayName: patient.fullName,
			isActive: true,
			readOnly: false,
			userType: "Patient",
			contactType: "User",
			hasEmailAddress: patient.hasEmailAddress,
		} as Participant
	}, [patient])
	const isReady = isFetched && sender
	const chatProviderValue = useMemo<Partial<ChatContextType>>(
		() => ({
			patient,
			sender,
			activeParticipant,
		}),
		[activeParticipant, patient, sender],
	)

	if (!isReady || !sender || !patient || !flags.chatMessageInApp.enabled) {
		return null
	}

	return (
		<ChatProvider defaultValue={chatProviderValue}>
			{({ threadId, noPriorComms, toggleThread }) => (
				<div className="flex h-full min-h-0 min-w-0 flex-col gap-2">
					{threadId && (
						<div className="px-5 py-2">
							<Button
								icon={<LeftOutlined aria-hidden />}
								onClick={() => toggleThread(null)}
							>
								Return to feed
							</Button>
						</div>
					)}
					<ChatList
						className={cn("px-1", !threadId && "grow")}
						senderRoles={["Patient"]}
					/>

					{noPriorComms && <NoPriorComms />}

					{!threadId && <ChatInput className="m-0" />}
				</div>
			)}
		</ChatProvider>
	)
}

function NoPriorComms() {
	return (
		<div className="max-w-[70%] min-w-0 rounded-xl rounded-bl-xs border border-grey-20 px-3 py-2 whitespace-pre-wrap text-navy-100">
			This will be your first message! The message will be sent directly to your
			medical team.
		</div>
	)
}
